import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FilterBarComponent } from '@modules/filters/filter-bar/filter-bar.component';
import { FilterButtonsComponent } from '@modules/filters/filter-buttons/filter-buttons.component';
import { FilterTabsComponent } from '@modules/filters/filter-tabs/filter-tabs.component';
import { PatientListComponent } from '@modules/patient/patient-list/patient-list.component';
import { ScrollHeaderTitleDirective } from '@modules/shared/directives/scroll-header-title.directive';
import { DropdownButtonComponent } from '@modules/shared/dropdown-button/dropdown-button.component';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { PAGINATED_LIST_SOURCE } from '@modules/shared/paginated-list/paginated-list.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { PaginationQuery } from '@pixacare/shared';
import { PatientStorePaginationService } from '@services/patient-store-pagination.service';
import { ContextualComponentMode } from '@shared/models/enums/contextual-component-mode.enum';
import { SortOrderStrategy } from '@shared/models/enums/sort-order-strategy.enum';
import { patientFilterTemplates } from '@shared/models/filters/filter-template.config';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { patientPaginationContext } from '@shared/models/pagination/pagination-context.config';
import { JoinPipe } from '@shared/pipes/join.pipe';
import { selectDepartmentName } from '@shared/store/departments/departments.selectors';
import { selectHasLicenseWriteAccess } from '@shared/store/licenses/licenses.selectors';
import { patientsActions } from '@shared/store/patients/patients.actions';
import { selectIsPatientStateLoaded } from '@shared/store/patients/patients.selectors';
import { selectClientCode, selectDepartmentId } from '@shared/store/router/router.selectors';
import { HEADER_BREADCRUMB_SEPARATOR } from '@shared/utils/header-utils';
import { TuiButton, TuiTitle } from '@taiga-ui/core';
import { TuiHeader, TuiNavigation } from '@taiga-ui/layout';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { FILTER_TEMPLATES, FilterBarService } from '../../filters/filter-bar.service';
import { PatientCreationService } from './patient-creation.service';
import { patientOrderings } from './patient-order.config';
import { PatientOrderService } from './patient-order.service';

@UntilDestroy()
@Component({
  templateUrl: './patients.component.html',
  providers: [
    FilterBarService,
    {
      provide: PAGINATED_LIST_SOURCE,
      useExisting: PatientStorePaginationService,
    },
    {
      deps: [Store],
      provide: FILTER_TEMPLATES,
      useFactory: patientFilterTemplates,
    },
    JoinPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    PatientListComponent,
    LoaderComponent,
    TuiButton,
    TuiHeader,
    TuiTitle,
    TuiNavigation,
    FilterTabsComponent,
    FilterBarComponent,
    FilterButtonsComponent,
    ScrollHeaderTitleDirective,
    TranslocoDirective,
    DropdownButtonComponent,
  ],
})
export class PatientsComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly patientPaginationService = inject(PatientStorePaginationService);
  private readonly filterBarService = inject(FilterBarService);
  private readonly patientCreationService = inject(PatientCreationService);
  private readonly patientOrderService = inject(PatientOrderService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly translocoService = inject(TranslocoService);

  readonly hasLicenseWriteAccess$: Observable<boolean> = this.store.select(selectHasLicenseWriteAccess);

  readonly ContextualComponentMode = ContextualComponentMode;

  order$: Observable<SortOrderStrategy>;

  readonly clientCode$ = this.store.select(selectClientCode).pipe(distinctUntilChanged());
  readonly departmentId$ = this.store.select(selectDepartmentId).pipe(distinctUntilChanged());

  readonly isDataLoaded$: Observable<boolean> = this.clientCode$.pipe(
    switchMap((clientCode) => this.store.select(selectIsPatientStateLoaded(clientCode))),
  );

  readonly mode$: Observable<ContextualComponentMode> = this.departmentId$.pipe(
    map((departmentId) => (departmentId ? ContextualComponentMode.DEPARTMENT : ContextualComponentMode.USER)),
  );

  private readonly params$ = combineLatest({
    clientCode: this.clientCode$,
    departmentId: this.departmentId$,
  });

  private readonly departmentName$ = this.params$.pipe(
    switchMap(({ clientCode, departmentId }) => this.store.select(selectDepartmentName(clientCode, departmentId))),
  );

  readonly headerTitle$ = combineLatest([this.departmentName$, this.mode$]).pipe(
    map(([departmentName, mode]) =>
      mode === ContextualComponentMode.USER ? this.translocoService.translate('Mes patients') : departmentName,
    ),
  );

  readonly headerSubtitle$ = this.mode$.pipe(
    map((mode) =>
      mode === ContextualComponentMode.DEPARTMENT ? this.translocoService.translate('Patients du groupe') : null,
    ),
  );

  readonly orderMenuActions: Record<string, MenuAction> = this.patientOrderService.orderMenuActions;

  readonly scrollHeaderTitle$ = combineLatest([
    this.headerTitle$,
    this.headerSubtitle$,
    this.filterBarService.selectedTabValue$,
  ]).pipe(
    map(([headerTitle, headerSubtitle, filterTabValue]) =>
      this.joinPipe.transform(
        [headerTitle, ...(headerSubtitle ? [headerSubtitle] : []), ...(filterTabValue ? [filterTabValue] : [])],
        HEADER_BREADCRUMB_SEPARATOR,
      ),
    ),
  );

  ngOnInit(): void {
    this.filterBarService.init();

    this.store.dispatch(patientsActions.resetPatientsState());
    this.order$ = this.patientOrderService.getPatientOrder();

    combineLatest([this.filterBarService.filteredSearch$, this.params$, this.order$])
      .pipe(untilDestroyed(this))
      .subscribe(([{ search, filters }, { clientCode, departmentId }, order]) => {
        this.patientPaginationService.load(clientCode, {
          query: new PaginationQuery({
            orderBy: patientOrderings[order],
            filter: [
              ...(departmentId ? [{ prop: 'department_id', op: 'eq', val: departmentId.toString() }] : []),
              ...filters,
            ],
            size: patientPaginationContext.countPerPage,
            search,
          }),
          reset: true,
        });
      });
  }

  createPatient(clientCode: string): void {
    this.patientCreationService.createPatient(clientCode);
  }
}
